import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import {
  Box, Button, ButtonBase, Drawer, IconButton, Popover, Skeleton, Stack, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, ArrowDown01Icon, ArrowLeft01Icon, ArrowRight01Icon} from "@hugeicons/core-free-icons";
import {CampaignAgenda} from "./CampaignAgenda";
import {CampaignStatTiles} from "./CampaignStatTiles";
import {PANEL_SX} from "../../../dashboard/components/Panel";
import {CampaignCalendarDetails} from "./CampaignCalendarDetails";
import {campaignColor, campaignRunsOn, campaignState, monthWeeks, STATE_TINTS} from "./calendarUtils";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const GAP = 3;
const CHIP_HEIGHT = 20;
const CHIP_SLOT = 23;
const DAY_HEADER = 28;

const isoDate = (day) => day.format("YYYY-MM-DD");
const canCreateOn = (day) => !day.isBefore(moment(), "day");

const navButtonSx = {
  width: 30, height: 30, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)",
  "&:hover": {bgcolor: "rgba(132, 145, 247, 0.24)"},
};

const MonthPicker = ({month, anchorEl, onClose, onSelect}) => {
  const [year, setYear] = useState(month.year());
  const now = moment();

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionProps={{onEnter: () => setYear(month.year())}}
      anchorOrigin={{vertical: "bottom", horizontal: "left"}}
      PaperProps={{sx: {mt: 1, p: 1.5, width: 280, borderRadius: "18px", boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
    >
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 1.25}}>
        <IconButton onClick={() => setYear(year - 1)} aria-label={"Previous year"} sx={navButtonSx}>
          <HugeiconsIcon icon={ArrowLeft01Icon} size={17}/>
        </IconButton>
        <Typography sx={{fontSize: 18, fontWeight: 800, color: "#23264D"}}>{year}</Typography>
        <IconButton onClick={() => setYear(year + 1)} aria-label={"Next year"} sx={navButtonSx}>
          <HugeiconsIcon icon={ArrowRight01Icon} size={17}/>
        </IconButton>
      </Stack>
      <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.75}}>
        {moment.monthsShort().map((name, index) => {
          const selected = month.year() === year && month.month() === index;
          const current = now.year() === year && now.month() === index;
          return (
            <ButtonBase key={name} onClick={() => onSelect(moment({year, month: index, day: 1}))} sx={{
              height: 44, borderRadius: "12px", fontSize: 14, fontWeight: selected || current ? 700 : 500,
              color: selected ? "#fff" : current ? "#4B55C9" : "#23264D",
              background: selected ? "#8491F7" : current ? "rgba(132, 145, 247, 0.18)" : "#F6F7FE",
              "&:hover": {filter: "brightness(0.97)"},
            }}>
              {name}
            </ButtonBase>
          );
        })}
      </Box>
    </Popover>
  );
};

const CalendarHeader = ({month, onPrev, onNext, onToday, onSelectMonth}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}>
      <ButtonBase
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={"Choose month and year"}
        sx={{gap: 0.75, px: 1, py: 0.5, mx: -1, borderRadius: "12px", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.1)"}}}
      >
        <Typography component={"h2"} sx={{
          fontSize: {xs: 18, md: 20}, fontWeight: 800, letterSpacing: "-0.02em", color: "#4B55C9",
        }}>
          {month.format("MMMM YYYY")}
        </Typography>
        <Box sx={{
          display: "flex", color: "#8491F7", transform: anchorEl ? "rotate(180deg)" : "none",
          transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}>
          <HugeiconsIcon icon={ArrowDown01Icon} size={18}/>
        </Box>
      </ButtonBase>
      <MonthPicker
        month={month}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onSelect={(next) => {
          setAnchorEl(null);
          onSelectMonth(next);
        }}
      />
      <Stack direction={"row"} alignItems={"center"} gap={0.75}>
        <Button onClick={onToday} sx={{
          height: 30, px: 1.5, borderRadius: "999px", textTransform: "none", fontSize: 12, fontWeight: 700, color: "#4B55C9",
          bgcolor: "rgba(132, 145, 247, 0.16)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.26)"},
        }}>
          Today
        </Button>
        <IconButton onClick={onPrev} aria-label={"Previous month"} sx={navButtonSx}>
          <HugeiconsIcon icon={ArrowLeft01Icon} size={17}/>
        </IconButton>
        <IconButton onClick={onNext} aria-label={"Next month"} sx={navButtonSx}>
          <HugeiconsIcon icon={ArrowRight01Icon} size={17}/>
        </IconButton>
      </Stack>
    </Stack>
  );
};

const DesktopWeek = ({week, month, campaigns, selectedId, onSelect, range, onDragStart, onDragEnter}) => {
  const days = useMemo(() => week.map(day => ({
    day,
    running: campaigns.filter(campaign => campaignRunsOn(campaign, day)),
  })), [week, campaigns]);
  const busiest = Math.max(1, ...days.map(item => item.running.length));
  const height = DAY_HEADER + busiest * CHIP_SLOT + 6;

  return (
    <Box sx={{display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", columnGap: `${GAP}px`, flex: "1 1 0", minHeight: height}}>
      {days.map(({day, running}) => {
        const isToday = day.isSame(moment(), "day");
        const inMonth = day.isSame(month, "month");
        const weekend = day.isoWeekday() >= 6;
        const selectable = canCreateOn(day);
        const showHint = selectable && running.length === 0 && !range;
        const inRange = Boolean(range) && !day.isBefore(range.start, "day") && !day.isAfter(range.end, "day");
        const rangeEdge = inRange && (day.isSame(range.start, "day") || day.isSame(range.end, "day"));
        return (
          <Box
            key={isoDate(day)}
            onMouseDown={selectable ? (event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              onDragStart(day);
            } : undefined}
            onMouseEnter={() => onDragEnter(day)}
            role={selectable ? "button" : undefined}
            aria-label={selectable ? `Create a campaign from ${day.format("D MMMM")}` : undefined}
            sx={{
              position: "relative", minHeight: height, borderRadius: "10px", p: 0.5, minWidth: 0,
              cursor: selectable ? "pointer" : "default", transition: "background-color .15s ease",
              bgcolor: inRange ? "rgba(132, 145, 247, 0.34)" : isToday ? "rgba(132, 145, 247, 0.2)" : inMonth ? "#F6F7FE" : "#FBFBFE",
              backgroundImage: weekend && !isToday
                ? "repeating-linear-gradient(135deg, rgba(132, 145, 247, 0.09) 0, rgba(132, 145, 247, 0.09) 5px, transparent 5px, transparent 10px)"
                : "none",
              boxShadow: rangeEdge ? "inset 0 0 0 2px #8491F7" : isToday ? "inset 0 0 0 2px rgba(132, 145, 247, 0.6)" : "none",
              "&:hover": selectable && !range ? {bgcolor: "rgba(132, 145, 247, 0.14)"} : {},
              "&:hover .create-hint": {opacity: 1, transform: "scale(1)"},
            }}
          >
            <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{mb: 0.5}}>
              <Box sx={{
                width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: isToday ? 700 : 600, color: isToday ? "#fff" : inMonth ? "#23264D" : "#B4B4C3",
                bgcolor: isToday ? "#8491F7" : "transparent",
                boxShadow: "none",
              }}>
                {day.date()}
              </Box>
              {isToday && (
                <Typography sx={{fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: "#4B55C9"}}>TODAY</Typography>
              )}
            </Stack>
            {showHint && (
              <Box className={"create-hint"} sx={{
                position: "absolute", top: 5, right: 5, width: 20, height: 20, borderRadius: "50%", display: "flex",
                alignItems: "center", justifyContent: "center", color: "#fff", bgcolor: "#8491F7",
                opacity: 0, transform: "scale(0.6)", transition: "opacity .15s ease, transform .25s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}>
                <HugeiconsIcon icon={Add01Icon} size={13}/>
              </Box>
            )}
            <Stack gap={`${CHIP_SLOT - CHIP_HEIGHT}px`}>
              {running.map(campaign => {
                const tint = campaignColor(campaign);
                const state = campaignState(campaign);
                const selected = String(selectedId) === String(campaign.id);
                return (
                  <ButtonBase
                    key={campaign.id}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(campaign);
                    }}
                    title={`${campaign.name} · ${campaign.startTime} – ${campaign.endTime}`}
                    sx={{
                      height: CHIP_HEIGHT, width: "fit-content", maxWidth: "100%", alignSelf: "flex-start", px: 1, gap: 0.75,
                      justifyContent: "flex-start", overflow: "hidden", textAlign: "left", borderRadius: "999px",
                      bgcolor: tint.bg, color: tint.color, opacity: state === "FINISHED" ? 0.72 : 1,
                      boxShadow: selected ? `0 0 0 2px ${tint.dot}` : "none",
                      transition: "filter .15s ease",
                      "&:hover": {filter: "brightness(0.96)"},
                    }}
                  >
                    <Box component={"span"} sx={{width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: tint.dot}}/>
                    <Typography noWrap sx={{fontSize: 11, fontWeight: 700, color: "inherit", lineHeight: 1}}>
                      {campaign.name}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

const DesktopCalendar = ({month, campaigns, selectedId, onSelect, onCreate}) => {
  const weeks = useMemo(() => monthWeeks(month), [month]);
  const [drag, setDrag] = useState(null);

  const range = useMemo(() => {
    if (!drag) return null;
    const today = moment().startOf("day");
    const first = moment.min(drag.anchor, drag.current);
    return {start: moment.max(first, today), end: moment.max(drag.anchor, drag.current)};
  }, [drag]);

  useEffect(() => {
    if (!range) return undefined;
    const finish = () => {
      setDrag(null);
      onCreate(isoDate(range.start), isoDate(range.end));
    };
    window.addEventListener("mouseup", finish);
    return () => window.removeEventListener("mouseup", finish);
  }, [range, onCreate]);

  return (
    <Stack gap={`${GAP}px`} sx={{flex: 1, minHeight: 0, userSelect: "none"}}>
      <Box sx={{display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", columnGap: `${GAP}px`, pb: 0.5}}>
        {WEEKDAYS.map((name, index) => (
          <Typography key={name} sx={{
            px: 0.75, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            color: index >= 5 ? "#8491F7" : "#8A8AA0",
          }}>
            {name}
          </Typography>
        ))}
      </Box>
      {weeks.map(week => (
        <DesktopWeek key={isoDate(week[0])} week={week} month={month} campaigns={campaigns}
                     selectedId={selectedId} onSelect={onSelect} range={range}
                     onDragStart={(day) => setDrag({anchor: day, current: day})}
                     onDragEnter={(day) => setDrag(previous => (previous ? {...previous, current: day} : previous))}/>
      ))}
    </Stack>
  );
};

const MobileCalendar = ({month, campaigns, selectedDay, onSelectDay, onSelect, onCreate}) => {
  const weeks = useMemo(() => monthWeeks(month), [month]);
  const dayCampaigns = useMemo(() => campaigns.filter(campaign => campaignRunsOn(campaign, selectedDay)), [campaigns, selectedDay]);

  return (
    <Stack gap={1.5}>
      <Box sx={{display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))"}}>
        {WEEKDAYS.map((name, index) => (
          <Typography key={name} sx={{
            textAlign: "center", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            color: index >= 5 ? "#8491F7" : "#8A8AA0",
          }}>
            {name.slice(0, 2).toUpperCase()}
          </Typography>
        ))}
      </Box>
      <Box sx={{display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", rowGap: 0.5}}>
        {weeks.flat().map(day => {
          const isToday = day.isSame(moment(), "day");
          const selected = day.isSame(selectedDay, "day");
          const inMonth = day.isSame(month, "month");
          const running = campaigns.filter(campaign => campaignRunsOn(campaign, day));
          return (
            <ButtonBase key={isoDate(day)} onClick={() => onSelectDay(day)} sx={{
              height: 40, flexDirection: "column", gap: 0.25, borderRadius: "12px", justifyContent: "center",
              bgcolor: selected ? "rgba(132, 145, 247, 0.2)" : day.isoWeekday() >= 6 && inMonth ? "rgba(132, 145, 247, 0.07)" : "transparent",
            }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: isToday || selected ? 700 : 500,
                color: isToday ? "#fff" : inMonth ? "#23264D" : "#B4B4C3",
                bgcolor: isToday ? "#8491F7" : "transparent",
                boxShadow: "none",
              }}>
                {day.date()}
              </Box>
              <Stack direction={"row"} gap={"3px"} sx={{height: 5}}>
                {running.slice(0, 3).map(campaign => (
                  <Box key={campaign.id} sx={{width: 5, height: 5, borderRadius: "50%", bgcolor: campaignColor(campaign).dot}}/>
                ))}
              </Stack>
            </ButtonBase>
          );
        })}
      </Box>

      <Box>
        <Typography sx={{mb: 1, fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>
          {selectedDay.format("dddd, D MMMM")} · {dayCampaigns.length} {dayCampaigns.length === 1 ? "campaign" : "campaigns"}
        </Typography>
        <Stack gap={1}>
          {dayCampaigns.length === 0 && (
            <Stack alignItems={"center"} gap={1.5} sx={{py: 2}}>
              <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>No campaigns on this day</Typography>
              {canCreateOn(selectedDay) && (
                <Button
                  onClick={() => onCreate(isoDate(selectedDay))}
                  startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                  sx={{
                    height: 40, px: 2, borderRadius: "999px", textTransform: "none", fontSize: 13, fontWeight: 700,
                    color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.16)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.26)"},
                  }}
                >
                  Create a campaign on {selectedDay.format("D MMM")}
                </Button>
              )}
            </Stack>
          )}
          {dayCampaigns.map(campaign => {
            const tint = STATE_TINTS[campaignState(campaign)];
            const color = campaignColor(campaign);
            return (
              <ButtonBase key={campaign.id} onClick={() => onSelect(campaign)} sx={{
                width: "100%", justifyContent: "space-between", gap: 1.5, textAlign: "left", px: 1.5, py: 1.25,
                borderRadius: "16px", bgcolor: color.bg,
              }}>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{minWidth: 0}}>
                  <Box sx={{width: 10, height: 10, borderRadius: "50%", flexShrink: 0, bgcolor: color.dot}}/>
                  <Box sx={{minWidth: 0}}>
                    <Typography noWrap sx={{fontSize: 14, fontWeight: 700, color: color.color}}>{campaign.name}</Typography>
                    <Typography sx={{fontSize: 12, color: color.color, opacity: 0.8}}>
                      {campaign.startTime} – {campaign.endTime}
                    </Typography>
                  </Box>
                </Stack>
                <Box component={"span"} sx={{
                  px: 1, height: 22, display: "inline-flex", alignItems: "center", borderRadius: "999px", fontSize: 11,
                  fontWeight: 700, flexShrink: 0, color: tint.color, bgcolor: "#fff",
                }}>
                  {tint.label}
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export const CampaignsCalendar = ({campaigns, loading, getExtension, subProjectOf, onOpen, onEdit, onCreate}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [month, setMonth] = useState(() => moment().startOf("month"));
  const [selectedDay, setSelectedDay] = useState(() => moment().startOf("day"));
  const [selectedId, setSelectedId] = useState(null);

  const dated = useMemo(() => campaigns.filter(campaign => campaign.startDate && campaign.endDate), [campaigns]);
  const selected = dated.find(campaign => String(campaign.id) === String(selectedId)) || null;

  const goToMonth = (next) => {
    setMonth(next);
    setSelectedDay(current => current.isSame(next, "month") ? current : next.isSame(moment(), "month") ? moment().startOf("day") : next.clone());
  };

  const details = selected && (
    <CampaignCalendarDetails
      campaign={selected}
      extension={getExtension(selected.extensionId)}
      language={subProjectOf?.(selected)}
      onClose={() => setSelectedId(null)}
      onOpen={onOpen}
      onEdit={onEdit}
    />
  );

  if (loading && campaigns.length === 0) {
    return <Skeleton variant={"rounded"} height={420} sx={{borderRadius: "18px"}}/>;
  }

  const header = (
    <CalendarHeader
      month={month}
      onPrev={() => goToMonth(month.clone().subtract(1, "month"))}
      onNext={() => goToMonth(month.clone().add(1, "month"))}
      onSelectMonth={goToMonth}
      onToday={() => {
        goToMonth(moment().startOf("month"));
        setSelectedDay(moment().startOf("day"));
      }}
    />
  );

  if (isMobile) {
    return (
      <Box sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
        <Stack gap={1.5}>
          <CampaignStatTiles campaigns={dated} month={month}/>
          <Stack gap={1.25} sx={{...PANEL_SX, p: 1.5}}>
            {header}
            <MobileCalendar month={month} campaigns={dated} selectedDay={selectedDay} onSelectDay={setSelectedDay}
                            onSelect={(campaign) => setSelectedId(campaign.id)} onCreate={onCreate}/>
          </Stack>
        </Stack>
        <Drawer
          anchor={"bottom"}
          open={Boolean(selected)}
          onClose={() => setSelectedId(null)}
          PaperProps={{sx: {maxHeight: "85vh", borderRadius: "20px 20px 0 0", bgcolor: "#fff"}}}
        >
          {details}
        </Drawer>
      </Box>
    );
  }

  return (
    <Stack gap={2} sx={{flex: 1, minHeight: 0}}>
      <CampaignStatTiles campaigns={dated} month={month}/>
      <Box sx={{flex: 1, minHeight: 0, display: "flex", gap: 2}}>
        <Box sx={{...PANEL_SX, flex: 1, minWidth: 0, p: 1.75, display: "flex", flexDirection: "column", overflowY: "auto"}}>
          <Stack gap={1.25} sx={{flex: 1, minHeight: 0}}>
            {header}
            <DesktopCalendar month={month} campaigns={dated} selectedId={selectedId}
                             onSelect={(campaign) => setSelectedId(campaign.id)} onCreate={onCreate}/>
          </Stack>
        </Box>
        <Box sx={{...PANEL_SX, width: 340, flexShrink: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
          {details || <CampaignAgenda campaigns={dated} onSelect={(campaign) => setSelectedId(campaign.id)}/>}
        </Box>
      </Box>
    </Stack>
  );
};
